import React, { useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import Cookies from 'universal-cookie'
import {
    CAMPAIGN_TRACKING_CODE,
    TRACKING_ID_KEY_VALUE_SEPARATOR,
    TRACKING_ID_PAIR_SEPARATOR,
    UTM_PARAM_PREFIX
} from '../../constants/PDPConstants'
import WtbConst from '../../../common/constants/WtbEnum'
import { trackPDPData, trackDataOnLoad } from '../../services/TealiumService'
import { StateContext } from '../../services/StateService'
import '../../css/WhereToBuy.scss'
import { log } from '../../services/LogService'

const WhereToBuy = ({ ccTemplatedID, ccSubId, mmmId, locale, widgetType }) => {
    if (!ccSubId) return null
    const convertedLocale = locale.replace(/_/, '-')
    const language = locale.split('_')
    const templateID = ccTemplatedID?.charAt(0).toUpperCase() + ccTemplatedID?.slice(1)
    const cookies = new Cookies()
    const { dispatch } = useContext(StateContext)
    useEffect(() => {
        loadCCWTB()
        return () => {
            const existingScript = document.querySelector('#cci-widget')
            if (existingScript) {
                existingScript.remove()
            }
        }
    }, [])

    /**
     * Determines whether a URL parameter is a campaign tracking parameter,
     * meaning any generic parameter whose name begins with "utm_".
     * @param {string} paramKey - URL parameter name
     * @returns {boolean} - True when the parameter is a utm_* parameter
     */
    const isUtmParam = (paramKey) => paramKey.toLowerCase().startsWith(UTM_PARAM_PREFIX)

    /**
     * Builds the WTB tracking id from every utm_* parameter present in the URL,
     * keeping the order in which they appear.
     * @param {URLSearchParams} urlParams - Parameters of the current URL
     * @returns {string} - Tracking id, or an empty string when no utm_* params exist
     */
    const buildTrackingId = (urlParams) => {
        const trackingPairs = []
        for (const [paramKey, paramValue] of urlParams.entries()) {
            if (!isUtmParam(paramKey) || !paramValue?.trim()) {
                continue
            }
            trackingPairs.push(
                `${paramKey}${TRACKING_ID_KEY_VALUE_SEPARATOR}${paramValue}`
            )
        }

        return trackingPairs.join(TRACKING_ID_PAIR_SEPARATOR)
    }

    /* istanbul ignore next */
    const loadCCWTB = () => {
        // Creating the script
        log.debug(`CCI: ${widgetType}`)
        if (document.querySelector('#cci-widget')) {
            document.querySelector('#cci-widget').remove()
        }
        const CCWTBScript = document.createElement('script')
        CCWTBScript.type = 'text/javascript'
        CCWTBScript.src = WtbConst.CC_WIDGET_URL
        CCWTBScript.id = 'cci-widget'
        CCWTBScript.setAttribute('data-token', WtbConst.CC_TOKEN)
        CCWTBScript.setAttribute('data-locale', convertedLocale)
        CCWTBScript.setAttribute('data-displaylanguage', language[0])
        CCWTBScript.setAttribute('data-widgetid', templateID)
        CCWTBScript.setAttribute('data-man', mmmId)
        CCWTBScript.setAttribute('data-subid', ccSubId)
        CCWTBScript.addEventListener('load', () => {
            tealiumTracking()
            dispatch({ type: 'pdp_set_loading_CCWTBScript', payload: false })
        })
        CCWTBScript.addEventListener('error', () => {
            dispatch({ type: 'pdp_set_loading_CCWTBScript', payload: false })
        })
        const urlParams = new URLSearchParams(document.location.search)
        const trackingCode = buildTrackingId(urlParams)
        const cookieTrackingCode = cookies.get(CAMPAIGN_TRACKING_CODE)
        // Check to see if the cookie campaign_tracking_code is available or utm_* is in the url
        if (cookieTrackingCode || trackingCode) {
            // campaign_tracking_code is available or utm_* found in url, add it to WTB widget
            CCWTBScript.setAttribute(
                'data-trackingid',
                trackingCode || cookieTrackingCode
            )
            document
                .querySelector('.sps2-pdp_whereToBuy--container')
                .appendChild(CCWTBScript)
        } else {
            // No campaign_tracking_code found or utm_* in the url, init the CC WTB widget without it
            CCWTBScript.setAttribute('data-trackingid', 'undefined')
            document
                .querySelector('.sps2-pdp_whereToBuy--container')
                .appendChild(CCWTBScript)
        }
    }

    /* istanbul ignore next */
    const tealiumTracking = () => {
        window.CCEventTrackingCallback = (eventType, eventObj) => {
            log.debug(eventType, eventObj)
            switch (eventType) {
                case 'widget_open_impression':
                    trackDataOnLoad(
                        `PDP Commerce Connector: on load`,
                        'Conversion Event',
                        {
                            data_ean: mmmId,
                            data_displaylanguage: eventObj?.data_displaylanguage,
                            data_inline_widget: eventObj?.inline_widget,
                            data_token: eventObj?.api_token,
                            data_man: mmmId,
                            data_subid: eventObj?.data_subid,
                            data_widgetid: eventObj?.data_widgetid,
                            data_trackingid: eventObj?.data_trackingid
                        }
                    )
                    break
                case 'widget_other_impression':
                    trackPDPData(
                        `PDP Commerce Connector: store and shop list`,
                        'Conversion Event',
                        {
                            data_local_store_name_array: getArrayOfAttributes(
                                eventObj?.local_store_list,
                                'local_store_name'
                            ),
                            data_local_store_position_array: getArrayOfAttributes(
                                eventObj?.local_store_list,
                                'local_store_position'
                            ),
                            data_local_store_address_array: getArrayOfAttributes(
                                eventObj?.local_store_list,
                                'local_store_address'
                            ),
                            data_distance_to_store_array: getArrayOfAttributes(
                                eventObj?.local_store_list,
                                'distance_to_store'
                            ),
                            data_shop_name_array: getArrayOfAttributes(
                                eventObj?.shop_list,
                                'shop_name'
                            ),
                            data_shop_position_array: getArrayOfAttributes(
                                eventObj?.shop_list,
                                'shop_position'
                            ),
                            data_shop_delivery_time: getArrayOfAttributes(
                                eventObj?.shop_list,
                                'delivery_time'
                            )
                        }
                    )
                    break
                case 'store_clickout':
                    trackPDPData(
                        `PDP Commerce Connector: store list item click`,
                        'Conversion Event',
                        {
                            data_local_store_position: eventObj?.local_store_position,
                            data_clickout_type: eventObj?.clickout_type,
                            data_event_type: eventObj?.event_type,
                            data_inline_widget: eventObj?.inline_widget,
                            data_local_store_name: eventObj?.local_store_name,
                            data_local_store_address: eventObj?.local_store_address,
                            data_local_store_id: eventObj?.local_store_id
                        }
                    )
                    break
                case 'shop_clickout':
                    trackPDPData(
                        'PDP Commerce Connector: buy now click',
                        'Conversion Event',
                        {
                            data_event_type: eventObj?.event_type,
                            data_shop_name: eventObj?.shop_name,
                            data_delivery_time: eventObj?.delivery_time,
                            data_shop_position: eventObj?.shop_position,
                            data_shop_id: eventObj?.shop_id,
                            data_clickout_type: eventObj?.clickout_type
                        }
                    )
                    break
                default:
                    trackPDPData(
                        `PDP Commerce Connector: default event`,
                        'Conversion Event',
                        {
                            data_ean: eventObj?.data_ean,
                            data_displaylanguage: eventObj?.data_displaylanguage,
                            data_inline_widget: eventObj?.inline_widget,
                            data_token: eventObj?.api_token,
                            data_man: mmmId,
                            data_subid: eventObj?.data_subid,
                            data_widgetid: eventObj?.data_widgetid,
                            data_trackingid: eventObj?.data_trackingid,
                            data_event_type: eventObj?.event_type
                        }
                    )
                    break
            }
        }
    }

    /* istanbul ignore next */
    /**
     * Converts an object array in a attribute array
     * @param {*} array to get the attribute from
     * @param {*} attributeName attribute to be converted to an array
     * @returns Return array of attributes from an specific array
     */
    const getArrayOfAttributes = (array, attributeName) => {
        return array.map((elem) => elem[attributeName])
    }
    return <div className={`sps2-pdp_whereToBuy--container`}></div>
}

WhereToBuy.defaultProps = {
    ccTemplatedID: 'Emjo9gg796_7541ec30-33d4-11eb-987a-25087ad49e2c103'
}

WhereToBuy.propTypes = {
    ccTemplatedID: PropTypes.string
}

export default WhereToBuy
